import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { BookMarked, ChevronDown, ChevronUp } from "lucide-react";

function FindingRow({ f }) {
  const [open, setOpen] = useState(false);
  const approved = f.narrative_status === "Approved";
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`finding-${f.uid}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] text-slate-400">{f.uid}</span>
        <span className="text-xs font-bold text-slate-800 flex-1 min-w-[220px] leading-snug">{f.title}</span>
        <Badge variant="outline" className={`text-[9px] ${approved ? "bg-teal-50 text-teal-700 border-teal-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}
          data-testid={`finding-status-${f.uid}`}>{f.narrative_status}</Badge>
        <span className="font-mono text-[9px] text-slate-400">{f.consumer_modules} modules · v{f.narrative_version}</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mb-1">Single approved narrative — canonical wording</p>
            <p className="text-[12px] text-slate-700 leading-relaxed" data-testid={`finding-narrative-${f.uid}`}>{f.canonical_narrative}</p>
            <p className="text-[10px] text-slate-400 mt-1.5">{f.approval_basis} · {f.supporting.statements} statements ({f.supporting.eligible_statements} export-eligible) · {f.supporting.evidence_records} evidence records</p>
          </div>
          <div>
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mb-1.5">Consuming modules — reference by identifier, never a copy</p>
            <div className="space-y-1.5">
              {f.consumers.map((c) => (
                <div key={c.module} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]">
                  <span className="font-semibold text-slate-700 min-w-[180px]">{c.module}</span>
                  <span className="text-slate-500">{c.usage}</span>
                  <span className="font-mono text-[9px] text-indigo-600">{c.refs.join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CanonicalFindings() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/findings").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="bg-slate-50/60 border border-slate-200/80 rounded-xl p-5 space-y-4" data-testid="canonical-findings">
      <div>
        <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
          <BookMarked className="w-4 h-4 text-teal-600" />Canonical Finding register
          <span className="font-mono text-[10px] text-slate-400 font-normal">
            {data.summary.total} findings · {data.summary.approved} approved · {data.summary.module_references} module references
          </span>
        </h3>
        <p className="text-[11.5px] text-slate-600 mt-1 leading-relaxed max-w-3xl">{data.policy}</p>
      </div>
      <div className="space-y-2" data-testid="findings-list">
        {data.findings.map((f) => <FindingRow key={f.uid} f={f} />)}
      </div>
    </div>
  );
}
